<template>
    <div class="pay-management-page">
        <el-tabs v-model="activeTab" class="admin-tabs">
            <el-tab-pane label="财务收支概览" name="stats">
                <div v-if="statsLoading" class="loading-stats" v-loading="true"></div>
                <div v-else class="stats-content">
                    <!-- Summary Cards -->
                    <el-row :gutter="20" class="summary-cards">
                        <el-col :span="6">
                            <el-card shadow="hover" class="stat-card income">
                                <div class="stat-label">累计营收 (元)</div>
                                <div class="stat-value">¥ {{ statsSummary.total_income?.toFixed(2) }}</div>
                                <div class="stat-footer">成功收款: {{ statsSummary.total_paid }} 笔</div>
                            </el-card>
                        </el-col>
                        <el-col :span="6">
                            <el-card shadow="hover" class="stat-card refund">
                                <div class="stat-label">累计退款 (元)</div>
                                <div class="stat-value">¥ {{ statsSummary.total_refund?.toFixed(2) }}</div>
                                <div class="stat-footer">已退款: {{ statsSummary.total_refunded }} 笔</div>
                            </el-card>
                        </el-col>
                        <el-col :span="6">
                            <el-card shadow="hover" class="stat-card net">
                                <div class="stat-label">净利润 (元)</div>
                                <div class="stat-value">¥ {{ statsSummary.net_income?.toFixed(2) }}</div>
                                <div class="stat-footer">利润率: {{ calculateMargin() }}%</div>
                            </el-card>
                        </el-col>
                        <el-col :span="6">
                            <el-card shadow="hover" class="stat-card orders">
                                <div class="stat-label">全站总订单 (非活跃)</div>
                                <div class="stat-value">{{ statsSummary.total_orders }}</div>
                                <div class="stat-footer">含待支付及取消单</div>
                            </el-card>
                        </el-col>
                    </el-row>

                    <el-row :gutter="20">
                        <!-- Daily Table -->
                        <el-col :span="10">
                            <el-card shadow="never" class="table-card">
                                <template #header>
                                    <div class="card-header">
                                        <span>每日流水实况 (30天)</span>
                                        <el-button size="small" @click="fetchStats">刷新统计</el-button>
                                    </div>
                                </template>
                                <el-table :data="dailyStats" stripe height="500">
                                    <el-table-column prop="date" label="日期" width="120" />
                                    <el-table-column label="收/退">
                                        <template #default="scope">
                                            <div class="mini-stats">
                                                <span class="text-success">+{{ scope.row.income.toFixed(2) }}</span>
                                                <span class="text-danger">-{{ scope.row.refund.toFixed(2) }}</span>
                                            </div>
                                        </template>
                                    </el-table-column>
                                    <el-table-column prop="net_income" label="日净额" align="right">
                                        <template #default="scope">
                                            <b :class="scope.row.net_income >= 0 ? 'text-success' : 'text-danger'">
                                                {{ scope.row.net_income.toFixed(2) }}
                                            </b>
                                        </template>
                                    </el-table-column>
                                </el-table>
                            </el-card>
                        </el-col>

                        <!-- Individual Orders Logs -->
                        <el-col :span="14">
                            <el-card shadow="never" class="table-card">
                                <template #header>
                                    <div class="card-header"
                                        style="display: flex; justify-content: space-between; align-items: center;">
                                        <span>财务支付流水 (全量明细)</span>
                                        <el-button size="small" @click="fetchOrders"
                                            :loading="loadingOrders">刷新流水</el-button>
                                    </div>
                                </template>
                                <el-table :data="orders" v-loading="loadingOrders" stripe height="500">
                                    <el-table-column prop="out_trade_no" label="单号" width="180" show-overflow-tooltip />
                                    <el-table-column prop="total_fee" label="金额" width="80" />
                                    <el-table-column label="状态" width="160">
                                        <template #default="scope">
                                            <el-space>
                                                <el-tag :type="scope.row.finished ? 'success' : 'info'" size="small">
                                                    {{ scope.row.finished ? '已支付' : '待支付' }}
                                                </el-tag>
                                                <el-tag v-if="scope.row.refund_status > 0"
                                                    :type="scope.row.refund_status === 2 ? 'success' : 'warning'"
                                                    size="small">
                                                    {{ scope.row.refund_status === 2 ? '已退' : '退款中' }}
                                                </el-tag>
                                            </el-space>
                                        </template>
                                    </el-table-column>
                                    <el-table-column label="创建时间" width="160">
                                        <template #default="scope">
                                            {{ formatDate(scope.row.CreatedAt) }}
                                        </template>
                                    </el-table-column>
                                    <el-table-column label="操作" width="80" fixed="right">
                                        <template #default="scope">
                                            <el-button v-if="canRefund(scope.row)" type="danger" link size="small"
                                                @click="handleRefund(scope.row)">
                                                退款
                                            </el-button>
                                        </template>
                                    </el-table-column>
                                </el-table>
                            </el-card>
                        </el-col>
                    </el-row>
                </div>
            </el-tab-pane>

            <el-tab-pane label="支付网关连接测试" name="orders">
                <div class="pay-test-container">
                    <!-- Payment Generation Card -->
                    <el-card class="box-card">
                        <template #header>
                            <div class="card-header">
                                <span>YunGouOS Native Pay Test</span>
                            </div>
                        </template>
                        <div class="form-section">
                            <el-form :model="form" label-width="120px">
                                <el-form-item label="Payment Amount">
                                    <el-input-number v-model="form.amount" :precision="2" :step="0.01" :min="0.01" />
                                </el-form-item>
                                <el-form-item>
                                    <el-button type="primary" @click="generatePayment" :loading="loading">
                                        Generate QR Code
                                    </el-button>
                                </el-form-item>
                            </el-form>
                        </div>

                        <div v-if="qrCodeUrl" class="result-section">
                            <el-divider>Payment QR Code</el-divider>
                            <div class="qr-code-wrapper">
                                <img v-if="!paymentSuccess" :src="qrCodeUrl" class="qr-image" alt="QR Code">
                                <el-result v-else icon="success" title="Payment Successful"
                                    sub-title="Webhook received from YunGouOS" />

                                <p v-if="!paymentSuccess" class="qr-tip">Please scan with WeChat</p>
                                <p class="order-no">Order No: {{ orderNo }}</p>
                            </div>
                        </div>

                        <div v-if="webhookData" class="webhook-section">
                            <el-divider>Webhook Callback Data (Raw)</el-divider>
                            <el-card shadow="never" class="json-card">
                                <pre>{{ JSON.stringify(webhookData, null, 2) }}</pre>
                            </el-card>
                        </div>
                    </el-card>
                </div>
            </el-tab-pane>
        </el-tabs>
    </div>

    <!-- Refund Dialog -->
    <el-dialog v-model="refundDialogVisible" title="发起退款" width="400px">
        <el-form label-position="top">
            <el-form-item label="退款单号">
                <el-input disabled :value="currentRefundOrder?.out_trade_no" />
            </el-form-item>
            <el-form-item label="可退金额 (元)">
                <el-input disabled :value="currentRefundOrder?.total_fee" />
            </el-form-item>
            <el-form-item label="退款金额 (元)" required>
                <el-input-number v-model="refundAmount" :precision="2" :step="0.01" :min="0.01" :max="maxRefundValue" />
            </el-form-item>
        </el-form>
        <template #footer>
            <span class="dialog-footer">
                <el-button @click="refundDialogVisible = false">取消</el-button>
                <el-button type="primary" :loading="refundLoading" @click="submitRefund">
                    确认退款
                </el-button>
            </span>
        </template>
    </el-dialog>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import dayjs from 'dayjs'

const form = ref({
    amount: 0.01
})

const loading = ref(false)
const qrCodeUrl = ref('')
const orderNo = ref('')
const paymentSuccess = ref(false)
const webhookData = ref(null)
let pollingTimer = null

// New states for order list and refund
const orders = ref([])
const loadingOrders = ref(false)
const refundDialogVisible = ref(false)
const currentRefundOrder = ref(null)
const refundAmount = ref(0)
const maxRefundValue = ref(0)
const refundLoading = ref(false)

// Fetch orders on mount
const activeTab = ref('stats')
const statsLoading = ref(false)
const statsSummary = ref({})
const dailyStats = ref([])

const fetchStats = async () => {
    statsLoading.value = true
    try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/admin/pay/stats', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
        const data = await response.json()
        if (response.ok) {
            dailyStats.value = data.daily_stats || []
            statsSummary.value = data.summary || {}
        }
    } catch (error) {
        console.error('Failed to fetch stats:', error)
        ElMessage.error('无法获取财务汇总数据')
    } finally {
        statsLoading.value = false
    }
}

const calculateMargin = () => {
    if (!statsSummary.value.total_income || statsSummary.value.total_income === 0) return 0
    return ((statsSummary.value.net_income / statsSummary.value.total_income) * 100).toFixed(1)
}

onMounted(() => {
    fetchStats()
    fetchOrders()
})

const fetchOrders = async () => {
    loadingOrders.value = true
    try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/admin/pay/orders', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
        const data = await response.json()
        if (response.ok) {
            orders.value = data.orders || []
        }
    } catch (error) {
        console.error('Failed to fetch orders:', error)
    } finally {
        loadingOrders.value = false
    }
}

// Format dates
const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    // Note: The timestamp received from webhook is string, DB created_at is valid ISO
    return dayjs(dateStr).format('YYYY-MM-DD HH:mm:ss')
}

// Start polling for order status
const startPolling = (tradeNo) => {
    paymentSuccess.value = false
    webhookData.value = null

    // Clear any existing timer
    if (pollingTimer) {
        clearInterval(pollingTimer)
    }

    // Poll every 3 seconds
    pollingTimer = setInterval(async () => {
        try {
            const response = await fetch(`/api/pay/status?outTradeNo=${tradeNo}`)
            const data = await response.json()

            if (response.ok && data.code === 1) {
                // Payment successful
                paymentSuccess.value = true
                webhookData.value = data.webhook_data
                clearInterval(pollingTimer)
                ElMessage.success('支付成功！已接收到回调数据')
                fetchOrders() // Refresh the list
            }
        } catch (error) {
            console.error('Failed to poll order status', error)
        }
    }, 3000)
}

// Ensure timer is cleaned up when component is destroyed
onUnmounted(() => {
    if (pollingTimer) {
        clearInterval(pollingTimer)
    }
})

const generatePayment = async () => {
    if (form.value.amount <= 0) {
        ElMessage.warning('Amount must be greater than 0')
        return
    }

    loading.value = true
    qrCodeUrl.value = ''
    orderNo.value = ''
    paymentSuccess.value = false
    webhookData.value = null

    if (pollingTimer) {
        clearInterval(pollingTimer)
    }

    try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/pay/test', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                amount: form.value.amount.toString()
            })
        })

        const data = await response.json()

        if (response.ok) {
            qrCodeUrl.value = data.qr_url
            orderNo.value = data.out_trade_no
            ElMessage.success('Payment QR Code generated successfully')

            // Refresh table to show the new 'Pending' order
            fetchOrders()

            // Start polling as soon as QR code is generated
            startPolling(data.out_trade_no)
        } else {
            ElMessage.error(data.details || data.error || 'Failed to generate payment request')
        }
    } catch (error) {
        console.error('Payment Error:', error)
        ElMessage.error('Network error. Failed to call API.')
    } finally {
        loading.value = false
    }
}

// Calculate if order can still be refunded
const canRefund = (order) => {
    if (!order.finished) return false
    const paidAmount = order.money ? parseFloat(order.money) : parseFloat(order.total_fee || 0)
    const alreadyRefunded = order.refund_amount ? parseFloat(order.refund_amount) : 0
    return (paidAmount - alreadyRefunded) >= 0.01
}

// Open Refund Dialog
const handleRefund = (order) => {
    currentRefundOrder.value = order

    // Calculate max refundable amount: actual paid money (or total_fee) minus already refunded amount
    const paidAmount = order.money ? parseFloat(order.money) : parseFloat(order.total_fee)
    const alreadyRefunded = order.refund_amount ? parseFloat(order.refund_amount) : 0
    const maxRefund = (paidAmount - alreadyRefunded).toFixed(2)

    refundAmount.value = parseFloat(maxRefund)
    maxRefundValue.value = parseFloat(maxRefund)
    refundDialogVisible.value = true
}

// Submit Refund
const submitRefund = async () => {
    if (refundAmount.value <= 0) {
        ElMessage.warning('退款金额必须大于 0')
        return
    }

    refundLoading.value = true
    try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/admin/pay/refund', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                out_trade_no: currentRefundOrder.value.out_trade_no,
                money: refundAmount.value.toString()
            })
        })

        const data = await response.json()

        if (response.ok) {
            ElMessage.success('退款申请已提交')
            refundDialogVisible.value = false
            fetchOrders() // Refresh the list to reflect status
        } else {
            ElMessage.error(data.details || data.error || '退款失败')
        }
    } catch (error) {
        console.error('Refund Error:', error)
        ElMessage.error('网络错误，退款请求失败')
    } finally {
        refundLoading.value = false
    }
}
</script>

<style scoped>
.pay-management-page {
    padding: 24px;
    background-color: #f0f2f5;
    min-height: calc(100vh - 84px);
}

.admin-tabs {
    background: #fff;
    padding: 20px;
    border-radius: 8px;
}

.summary-cards {
    margin-bottom: 24px;
}

.stat-card {
    border-radius: 12px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: none;
    color: #fff;
}

.stat-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
}

.stat-card.income {
    background: linear-gradient(135deg, #1890ff 0%, #36cfc9 100%);
}

.stat-card.refund {
    background: linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%);
}

.stat-card.net {
    background: linear-gradient(135deg, #52c41a 0%, #95de64 100%);
}

.stat-card.orders {
    background: linear-gradient(135deg, #722ed1 0%, #b37feb 100%);
}

.stat-label {
    font-size: 14px;
    opacity: 0.9;
    margin-bottom: 8px;
}

.stat-value {
    font-size: 28px;
    font-weight: 700;
    margin-bottom: 12px;
    letter-spacing: -0.5px;
}

.stat-footer {
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    font-size: 12px;
    opacity: 0.8;
}

.table-card {
    border-radius: 12px;
    border: 1px solid #ebeef5;
    margin-bottom: 20px;
}

.mini-stats {
    display: flex;
    flex-direction: column;
    font-size: 12px;
}

.text-success {
    color: #52c41a;
    font-weight: 600;
}

.text-danger {
    color: #ff4d4f;
    font-weight: 600;
}

.loading-stats {
    height: 400px;
}

.pay-test-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 20px;
    padding: 20px 0;
}

.box-card {
    width: 100%;
    max-width: 500px;
}

.order-list-card {
    width: 100%;
    flex: 2 1 700px;
    /* Take up more space */
}

.card-header {
    font-size: 18px;
    font-weight: bold;
}

.form-section {
    padding: 20px 0;
}

.result-section {
    margin-top: 30px;
    text-align: center;
}

.qr-code-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 20px;
}

.qr-image {
    width: 250px;
    height: 250px;
    border: 1px solid #dcdfe6;
    border-radius: 8px;
    padding: 10px;
    background-color: #fff;
}

.qr-tip {
    margin-top: 15px;
    color: #606266;
    font-size: 14px;
}

.order-no {
    margin-top: 5px;
    color: #909399;
    font-size: 13px;
}
</style>
